import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
  Button,
} from '@mui/material';
import {
  ShoppingCart as OrdersIcon,
  Receipt as BillsIcon,
  AssignmentReturn as ReturnsIcon,
  AttachMoney as MoneyIcon,
  Refresh,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip as ReTooltip,
  ResponsiveContainer,
} from 'recharts';
import { PurchaseService } from '../services/purchaseService';
import { formatCurrency } from '../utils/formatters';
import PeriodSelector from '../components/common/PeriodSelector';
import { computePresetRange, DEFAULT_DATE_RANGE_PRESET } from '../utils/dateRangePresets';

interface DashboardStats {
  total_orders: number;
  total_orders_amount: number;
  pending_orders: number;
  pending_orders_amount: number;
  total_bills: number;
  total_bills_amount: number;
  unpaid_bills: number;
  unpaid_bills_amount: number;
  total_returns: number;
  total_returns_amount: number;
  top_suppliers: Array<{
    supplier_id: string;
    supplier_name: string;
    total_orders?: number;
    order_count?: number;
    total_amount: number;
  }>;
  order_status_breakdown: Record<string, number>;
  bill_status_breakdown: Record<string, number>;
  recent_orders: Array<any>;
  recent_bills: Array<any>;
}

const ORDER_STATUS_COLORS = ['#1976d2', '#ed6c02', '#9c27b0', '#2e7d32', '#0288d1', '#d32f2f', '#757575'];
const BILL_STATUS_COLORS = ['#2e7d32', '#ed6c02', '#1976d2', '#9c27b0', '#d32f2f', '#757575'];

export const PurchaseDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState(() => computePresetRange(DEFAULT_DATE_RANGE_PRESET));
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [error, setError] = useState<{ message: string; isForbidden: boolean } | null>(null);

  const fetchDashboardData = useCallback(async () => {
    if (stats) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const data = await PurchaseService.getPurchaseDashboard(dateRange.dateFrom, dateRange.dateTo);
      setStats(data as any);
    } catch (err: any) {
      const status = err?.response?.status;
      setError({
        message: status === 403
          ? "You don't have permission to view purchase data."
          : 'Failed to load dashboard data. Please try again.',
        isForbidden: status === 403
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const StatCard = ({ title, value, subtitle, icon, color }: any) => (
    <Card sx={{ height: '100%', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'divider' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="div">
              {value}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {subtitle}
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: `${color}.light`,
              borderRadius: 2,
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={260} height={48} sx={{ mb: 2 }} />
        <Grid container spacing={3} mb={3}>
          {[0, 1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={2.4} key={i}>
              <Skeleton variant="rounded" height={140} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={300} />
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box p={3}>
        <Alert severity={error?.isForbidden ? 'warning' : 'error'} action={
          !error?.isForbidden && (
            <Button color="inherit" size="small" onClick={fetchDashboardData}>Retry</Button>
          )
        }>
          {error?.message || 'Failed to load dashboard data.'}
        </Alert>
      </Box>
    );
  }

  const orderStatusData = Object.entries(stats.order_status_breakdown || {}).map(([status, count]) => ({
    name: status,
    value: count,
  }));
  const billStatusData = Object.entries(stats.bill_status_breakdown || {}).map(([status, count]) => ({
    name: status,
    value: count,
  }));

  return (
    <Box sx={{ p: 3, opacity: refreshing ? 0.6 : 1, transition: 'opacity 0.15s' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Purchase Dashboard
        </Typography>
        <Box display="flex" gap={2}>
          <PeriodSelector onChange={(dateFrom, dateTo) => setDateRange({ dateFrom, dateTo })} />
          <Tooltip title="Refresh">
            <IconButton onClick={fetchDashboardData} color="primary" disabled={refreshing}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error.message}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Total Orders"
            value={stats.total_orders || 0}
            subtitle={formatCurrency(stats.total_orders_amount || 0)}
            icon={<OrdersIcon color="primary" />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Pending Orders"
            value={stats.pending_orders || 0}
            subtitle={formatCurrency(stats.pending_orders_amount || 0)}
            icon={<OrdersIcon color="warning" />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Total Bills"
            value={stats.total_bills || 0}
            subtitle={formatCurrency(stats.total_bills_amount || 0)}
            icon={<BillsIcon color="success" />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Unpaid Bills"
            value={stats.unpaid_bills || 0}
            subtitle={formatCurrency(stats.unpaid_bills_amount || 0)}
            icon={<MoneyIcon color="error" />}
            color="error"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Returns"
            value={stats.total_returns || 0}
            subtitle={formatCurrency(stats.total_returns_amount || 0)}
            icon={<ReturnsIcon color="secondary" />}
            color="secondary"
          />
        </Grid>
      </Grid>

      {/* Status breakdown charts — visual parity with the other dashboards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Orders by Status</Typography>
              {orderStatusData.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={orderStatusData} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                      paddingAngle={3} dataKey="value">
                      {orderStatusData.map((_, i) => (
                        <Cell key={i} fill={ORDER_STATUS_COLORS[i % ORDER_STATUS_COLORS.length]} />
                      ))}
                    </Pie>
                    <ReTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height={260}>
                  <Typography color="text.secondary" variant="body2">No orders in this period</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Bills by Status</Typography>
              {billStatusData.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={billStatusData} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                      paddingAngle={3} dataKey="value">
                      {billStatusData.map((_, i) => (
                        <Cell key={i} fill={BILL_STATUS_COLORS[i % BILL_STATUS_COLORS.length]} />
                      ))}
                    </Pie>
                    <ReTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height={260}>
                  <Typography color="text.secondary" variant="body2">No bills in this period</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" gutterBottom>
              Top Suppliers
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Supplier</TableCell>
                    <TableCell align="right">Orders</TableCell>
                    <TableCell align="right">Total Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.top_suppliers?.length ? stats.top_suppliers.slice(0, 5).map((supplier: any) => (
                    <TableRow key={supplier.supplier_id}>
                      <TableCell>{supplier.supplier_name}</TableCell>
                      <TableCell align="right">{supplier.order_count ?? supplier.total_orders ?? 0}</TableCell>
                      <TableCell align="right">{formatCurrency(supplier.total_amount)}</TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">No supplier activity in this period</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" gutterBottom>
              Recent Purchase Orders
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>PO Number</TableCell>
                    <TableCell>Supplier</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.recent_orders?.length ? stats.recent_orders.slice(0, 5).map((order: any) => (
                    <TableRow key={order.id}>
                      <TableCell>{order.po_number}</TableCell>
                      <TableCell>{order.supplier_name || '-'}</TableCell>
                      <TableCell align="right">{formatCurrency(order.grand_total)}</TableCell>
                      <TableCell>
                        <Chip label={order.status} size="small" />
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">No recent orders</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" gutterBottom>
              Recent Bills
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Bill Number</TableCell>
                    <TableCell>Supplier</TableCell>
                    <TableCell>Bill Date</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell align="right">Total Amount</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.recent_bills?.length ? stats.recent_bills.slice(0, 10).map((bill: any) => (
                    <TableRow key={bill.id}>
                      <TableCell>{bill.bill_number}</TableCell>
                      <TableCell>{bill.supplier_name || '-'}</TableCell>
                      <TableCell>{new Date(bill.bill_date).toLocaleDateString()}</TableCell>
                      <TableCell>
                        {bill.due_date ? new Date(bill.due_date).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(bill.total_amount)}</TableCell>
                      <TableCell align="right">{formatCurrency(bill.balance_amount)}</TableCell>
                      <TableCell>
                        <Chip label={bill.status} size="small" />
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">No recent bills</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
